import classNames from 'classnames';
import Text from 'components/ui/Text';
import { useTranslation } from 'hooks/useTranslation';
import { FC } from 'react';
import {
  NormalizedValue,
  MainValueCategory,
  LightingSolutionCategory,
} from 'type';

type ColorType = 'financial' | 'human' | 'sustainability';

const getGridCols = (large: boolean) =>
  large
    ? 'grid-cols-[8rem_1fr] sm:grid-cols-[12rem_1fr] md:grid-cols-[16rem_1fr]'
    : 'grid-cols-2 md:grid-cols-[10rem_1fr] gap-4';

const getColorClass = (color: NormalizedValue, colorType: ColorType) => {
  const colors = {
    financial: [
      'bg-financial-1',
      'bg-financial-2',
      'bg-financial-3',
      'bg-financial-4',
      'bg-financial-5',
    ],
    human: [
      'bg-human-1',
      'bg-human-2',
      'bg-human-3',
      'bg-human-4',
      'bg-human-5',
    ],
    sustainability: [
      'bg-sustainability-1',
      'bg-sustainability-2',
      'bg-sustainability-3',
      'bg-sustainability-4',
      'bg-sustainability-5',
    ],
  };

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return colors[colorType][color - 1];
};

const getProgressBarColorClasses = (colorType: ColorType) => {
  const colors = {
    financial: {
      fill: 'fill-financial-1',
      stroke: 'stroke-financial-5',
    },
    human: {
      fill: 'fill-human-1',
      stroke: 'stroke-human-5',
    },
    sustainability: {
      fill: 'fill-sustainability-1',
      stroke: 'stroke-sustainability-5',
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  return colors[colorType];
};

const GraphLabels = ({ large }: { large: boolean }) => {
  const translation = useTranslation();

  return (
    <div className={`grid ${getGridCols(large)}`}>
      <div className="col-start-2 flex justify-between">
        <Text bold color="grey-500" className="small-caps leading-none mb-2">
          {translation('recipe.values.low')}
        </Text>
        <Text bold color="grey-500" className="small-caps leading-none mb-2">
          {translation('recipe.values.high')}
        </Text>
      </div>
    </div>
  );
};

const GraphBackground = ({ large }: { large: boolean }) => (
  <div className={`absolute grid h-full w-full ${getGridCols(large)}`}>
    <div className="col-start-2 grid-cols-5 grid border-grey-200 border-r-2">
      <div className="border-inherit border-l-2 h-full"></div>
      <div className="border-inherit border-dotted border-l-2 h-full"></div>
      <div className="border-inherit border-dotted border-l-2 h-full"></div>
      <div className="border-inherit border-dotted border-l-2 h-full"></div>
      <div className="border-inherit border-dotted border-l-2 h-full"></div>
    </div>
  </div>
);

type GraphBarProps = {
  className?: string;
  height: number;
  title: string;
  colorType: MainValueCategory;
  color: NormalizedValue;
  large: boolean;
  category: LightingSolutionCategory;
};

const GraphBar: FC<GraphBarProps> = (props) => {
  const { color, colorType, height, title, large, category } = props;
  const colorClass = getColorClass(color, colorType);
  const barClasses = classNames(
    'h-full w-full origin-left transition ease-bounce duration-700',
    colorClass,
  );

  const scaleY = category === 'none' ? 'scaleX(0)' : `scaleX(${height / 100})`;

  return (
    <div className={`grid mb-2 last:mb-0 ${getGridCols(large)}`}>
      <Text
        bold
        elementType="div"
        color="grey-500"
        size="label"
        className="leading-none h-6 small-caps -mt-[5px] flex items-center"
      >
        {title}
      </Text>
      <div className="z-10 relative h-5">
        <div style={{ transform: scaleY }} className={barClasses}></div>
      </div>
    </div>
  );
};

const ProgressBarCircle = ({
  values,
  colorType,
}: {
  values: {
    average: number;
    max: number;
  };
  colorType: ColorType;
}) => {
  const average = Math.floor(Number(values.average));
  // const percent = (average / values.max) * 100;
  const percent = (average / values.max) * 100;
  const dash = 100 - percent;
  const classes = getProgressBarColorClasses(colorType);

  return (
    <div className="relative flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="-1 -1 34 34"
        className="-rotate-90 w-8"
      >
        <circle
          cx="16"
          cy="16"
          r="15.6155"
          className={classNames(classes?.fill, classes?.stroke)}
          style={{
            strokeWidth: 3,
            strokeDasharray: '100 100',
            strokeDashoffset: `${dash}px`,
          }}
        />
      </svg>
      <span className="absolute font-bold text-md">{average}</span>
    </div>
  );
};

const GraphSummary = ({
  bars,
  value,
}: {
  bars: GraphBarProps[];
  value: number;
}) => {
  const translation = useTranslation();
  const category = bars[0].category;
  const colorType = bars[0].colorType;
  const total = bars.reduce((sum, item) => sum + item.color, 0);
  // const average = total / bars.length;
  const average = Math.floor(Number(total / bars.length));

  type NormalizedValuesAsString = `${NormalizedValue}`;
  const fixedAverage = average.toFixed(0) as NormalizedValuesAsString;
  const text = translation(`recipe.summary.${colorType}.${fixedAverage}`);

  return (
    <div
      className={classNames(
        'flex flex-row gap-3',
        category == 'none' && 'hidden',
      )}
    >
      <ProgressBarCircle
        values={{ average: value, max: 5 }}
        colorType={colorType}
      />
      <div className="text-[12px] leading-[1.2]">{text}</div>
    </div>
  );
};

type GraphProps = {
  large?: boolean;
  summary?: number | null;
  bars: GraphBarProps[];
};

const Graph: FC<GraphProps> = ({ large = false, summary = false, bars }) => (
  <>
    <GraphLabels large={large} />
    <div className="relative mb-6">
      <GraphBackground large={large} />
      {bars.map((bar, index) => (
        <GraphBar key={`graph-bar-${index}-${bar.category}`} {...bar} />
      ))}
    </div>
    {summary && (
      <div className="grid grid-cols-2  md:grid-cols-[10rem_1fr] gap-4">
        <div className="col-start-2 col-span-full">
          <GraphSummary bars={bars} value={Number(summary)} />
        </div>
      </div>
    )}
  </>
);

export default Graph;
